/**
 * Listado de sincronización de fichajes Factorial ↔ Rentman.
 *
 * Entradas: filtros empleado / fechas / sincronizado / activo vía fichajesService.
 * Salidas: tabla con reimport por fila y masivo (checks, uno a uno).
 * Casos limite: sin seleccion en bulk → toast; errores de API por item;
 *   select-all pide IDs de toda la busqueda (no solo la pagina).
 * Seguridad: solo admin (ruta PrivateRoute); no se muestran tokens.
 */
import React, { useEffect, useState } from "react";
import {
  Card,
  Table,
  Form,
  Button,
  Pagination,
  Badge,
  Spinner,
} from "react-bootstrap";
import { FaSyncAlt, FaFilter, FaDownload } from "react-icons/fa";
import { toast } from "react-toastify";
import { fichajesService } from "../services/api";

const PAGE_SIZE = 8;

/**
 * Formatea datetime API a es-ES.
 * @param {string|Date|null} value
 * @returns {string}
 */
function formatDateTime(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("es-ES", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Indica si la fila esta sincronizada con Rentman.
 * @param {object} row
 * @returns {boolean}
 */
function isSynced(row) {
  return row.RENTMAN_TIMEREGISTRATION_ID != null;
}

/**
 * Indica si el turno sigue abierto y, por tanto, no se puede sincronizar.
 * @param {object} row
 * @returns {boolean}
 */
function isOpenShift(row) {
  return row.FECHA_FIN == null;
}

/**
 * Construye params de filtro compartidos entre listado e IDs (sin paginacion).
 * @param {{ empleadoId: string, fechaDesde: string, fechaHasta: string, sincronizado: string, activo: string }} filters
 * @returns {object}
 */
function buildFilterParams(filters) {
  return {
    empleado_id: filters.empleadoId || undefined,
    fecha_desde: filters.fechaDesde || undefined,
    fecha_hasta: filters.fechaHasta || undefined,
    sincronizado: filters.sincronizado === "" ? undefined : filters.sincronizado,
    activo: filters.activo === "" ? undefined : filters.activo,
  };
}

const FichajesSyncList = () => {
  const [fichajes, setFichajes] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reimporting, setReimporting] = useState(false);
  const [selectingAll, setSelectingAll] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  // Tamano del conjunto completo de la ultima busqueda seleccionada (select-all).
  const [searchSelectableCount, setSearchSelectableCount] = useState(0);
  const [page, setPage] = useState(1);
  const [empleadoId, setEmpleadoId] = useState("");
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");
  const [sincronizado, setSincronizado] = useState("");
  const [activo, setActivo] = useState("true");
  const [pagination, setPagination] = useState({
    page: 1,
    limit: PAGE_SIZE,
    total: 0,
    pages: 1,
  });

  useEffect(() => {
    fetchEmpleados();
  }, []);

  // Al cambiar filtros (no pagina), se limpia la seleccion de toda la busqueda.
  useEffect(() => {
    setSelectedIds([]);
    setSearchSelectableCount(0);
  }, [empleadoId, fechaDesde, fechaHasta, sincronizado, activo]);

  useEffect(() => {
    fetchFichajes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, empleadoId, fechaDesde, fechaHasta, sincronizado, activo]);

  /**
   * Carga combo de empleados (CREW_ID).
   */
  const fetchEmpleados = async () => {
    try {
      const res = await fichajesService.getEmpleados();
      setEmpleados(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
      toast.error("Error al cargar empleados");
      setEmpleados([]);
    }
  };

  /**
   * Carga listado paginado con filtros actuales.
   * No vacia selectedIds: la seleccion de toda la busqueda se conserva al paginar.
   */
  const fetchFichajes = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: PAGE_SIZE,
        ...buildFilterParams({
          empleadoId,
          fechaDesde,
          fechaHasta,
          sincronizado,
          activo,
        }),
      };
      const res = await fichajesService.getAll(params);
      setFichajes(res.data.fichajes || []);
      if (res.data.pagination) {
        setPagination(res.data.pagination);
      }
    } catch (err) {
      console.error(err);
      toast.error(
        err.response?.data?.message || "Error al cargar fichajes"
      );
      setFichajes([]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Limpia filtros, seleccion y vuelve a pagina 1.
   */
  const clearFilters = () => {
    setEmpleadoId("");
    setFechaDesde("");
    setFechaHasta("");
    setSincronizado("");
    setActivo("true");
    setSelectedIds([]);
    setSearchSelectableCount(0);
    setPage(1);
  };

  /**
   * Vacía la seleccion actual (tras reimport o desmarcar cabecera).
   */
  const clearSelection = () => {
    setSelectedIds([]);
    setSearchSelectableCount(0);
  };

  /**
   * Toggle seleccion de una fila.
   * Si se destilda una fila tras select-all, la cabecera deja de marcarse
   * porque selectedIds.length deja de coincidir con searchSelectableCount.
   * @param {number} id
   */
  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  /**
   * Selecciona o deselecciona todos los IDs sincronizables de la busqueda
   * actual (mismos filtros), no solo la pagina visible.
   * @param {boolean} checked
   */
  const toggleSelectAll = async (checked) => {
    if (!checked) {
      clearSelection();
      return;
    }
    setSelectingAll(true);
    try {
      const res = await fichajesService.getIds(
        buildFilterParams({
          empleadoId,
          fechaDesde,
          fechaHasta,
          sincronizado,
          activo,
        })
      );
      const ids = Array.isArray(res.data?.ids) ? res.data.ids : [];
      setSelectedIds(ids);
      setSearchSelectableCount(ids.length);
      if (ids.length === 0) {
        toast.info("No hay fichajes sincronizables con estos filtros");
      }
    } catch (err) {
      console.error(err);
      toast.error(
        err.response?.data?.message || "Error al seleccionar toda la busqueda"
      );
      clearSelection();
    } finally {
      setSelectingAll(false);
    }
  };

  /**
   * Reimporta un fichaje (Factorial → BD → Rentman).
   * @param {number} id
   * @param {{ silent?: boolean }} options
   * @returns {Promise<boolean>}
   */
  const reimportOne = async (id, options = {}) => {
    try {
      const res = await fichajesService.reimport(id);
      if (!options.silent) {
        if (res.data.deleted) {
          toast.info(`Fichaje ${id}: eliminado en Factorial (marcado inactivo)`);
        } else {
          toast.success(
            `Fichaje ${id}: ${res.data.rentmanAction === "update" ? "actualizado" : "creado"} en Rentman`
          );
        }
      }
      return true;
    } catch (err) {
      const msg =
        err.response?.data?.message || `Error reimportando fichaje ${id}`;
      toast.error(msg);
      return false;
    }
  };

  /**
   * Boton por fila.
   * @param {number} id
   */
  const handleReimportRow = async (id) => {
    setReimporting(true);
    try {
      await reimportOne(id);
      clearSelection();
      await fetchFichajes();
    } finally {
      setReimporting(false);
    }
  };

  /**
   * Importacion masiva: uno a uno sobre la seleccion (toda la busqueda si select-all).
   */
  const handleBulkReimport = async () => {
    if (selectedIds.length === 0) {
      toast.warning("Selecciona al menos un fichaje");
      return;
    }
    setReimporting(true);
    let ok = 0;
    let fail = 0;
    try {
      for (const id of selectedIds) {
        const success = await reimportOne(id, { silent: true });
        if (success) ok += 1;
        else fail += 1;
      }
      toast.info(`Importacion finalizada: ${ok} ok, ${fail} error(es)`);
      clearSelection();
      await fetchFichajes();
    } finally {
      setReimporting(false);
    }
  };

  // Cabecera marcada si select-all cubre toda la busqueda, aunque la pagina
  // actual no tenga filas seleccionables (p. ej. solo turnos abiertos).
  const allSelected =
    searchSelectableCount > 0 &&
    selectedIds.length === searchSelectableCount;

  return (
    <div className="container-fluid py-3">
      <Card>
        <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
          <div className="d-flex align-items-center gap-2">
            <FaSyncAlt />
            <strong>Sincronización fichajes</strong>
          </div>
          <Button
            variant="primary"
            size="sm"
            disabled={reimporting || selectedIds.length === 0}
            onClick={handleBulkReimport}
          >
            {reimporting ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />{" "}
                Importando...
              </>
            ) : (
              <>
                <FaDownload className="me-1" /> Importar de Factorial
                {selectedIds.length > 0 ? ` (${selectedIds.length})` : ""}
              </>
            )}
          </Button>
        </Card.Header>
        <Card.Body>
          <div className="filter-section mb-3">
            <div className="d-flex align-items-center mb-2 gap-2">
              <FaFilter />
              <span>Filtros</span>
            </div>
            <div className="row g-2">
              <div className="col-md-3">
                <Form.Label>Empleado</Form.Label>
                <Form.Select
                  value={empleadoId}
                  onChange={(e) => {
                    setEmpleadoId(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">Todos</option>
                  {empleados.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.nombre}
                    </option>
                  ))}
                </Form.Select>
              </div>
              <div className="col-md-2">
                <Form.Label>Desde</Form.Label>
                <Form.Control
                  type="date"
                  value={fechaDesde}
                  onChange={(e) => {
                    setFechaDesde(e.target.value);
                    setPage(1);
                  }}
                />
              </div>
              <div className="col-md-2">
                <Form.Label>Hasta</Form.Label>
                <Form.Control
                  type="date"
                  value={fechaHasta}
                  onChange={(e) => {
                    setFechaHasta(e.target.value);
                    setPage(1);
                  }}
                />
              </div>
              <div className="col-md-1">
                <Form.Label>Sincronizado</Form.Label>
                <Form.Select
                  value={sincronizado}
                  onChange={(e) => {
                    setSincronizado(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">Todos</option>
                  <option value="true">Sí</option>
                  <option value="false">No</option>
                </Form.Select>
              </div>
              <div className="col-md-1">
                <Form.Label>Activo</Form.Label>
                <Form.Select
                  value={activo}
                  onChange={(e) => {
                    setActivo(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">Todos</option>
                  <option value="true">Sí</option>
                  <option value="false">No</option>
                </Form.Select>
              </div>
              <div className="col-auto d-flex align-items-end">
                <Button
                  variant="outline-secondary"
                  className="text-nowrap"
                  onClick={clearFilters}
                >
                  LIMPIAR
                </Button>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          ) : (
            <>
              <div className="d-none d-md-block">
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th style={{ width: 40 }}>
                        <Form.Check
                          type="checkbox"
                          checked={allSelected}
                          disabled={selectingAll || reimporting}
                          onChange={(e) => toggleSelectAll(e.target.checked)}
                          aria-label="Seleccionar toda la busqueda"
                          title="Seleccionar todos los resultados de la busqueda"
                        />
                      </th>
                      <th>Empleado</th>
                      <th>Inicio</th>
                      <th>Fin</th>
                      <th>Minutos</th>
                      <th>Activo</th>
                      <th>Sincronizado</th>
                      <th style={{ width: 120 }}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fichajes.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="text-center text-muted">
                          No hay fichajes con estos filtros
                        </td>
                      </tr>
                    ) : (
                      fichajes.map((row) => (
                        <tr key={row.ID}>
                          <td>
                            <Form.Check
                              type="checkbox"
                              checked={selectedIds.includes(row.ID)}
                              onChange={() => toggleSelect(row.ID)}
                              disabled={isOpenShift(row)}
                              aria-label={`Seleccionar fichaje ${row.ID}`}
                              title={
                                isOpenShift(row)
                                  ? "Los turnos abiertos no se pueden sincronizar"
                                  : "Seleccionar fichaje"
                              }
                            />
                          </td>
                          <td>{row.EMPLEADO_NOMBRE || row.CREWMEMBER}</td>
                          <td>{formatDateTime(row.FECHA_INICIO)}</td>
                          <td>{formatDateTime(row.FECHA_FIN)}</td>
                          <td>{row.MINUTOS ?? "-"}</td>
                          <td>
                            <Badge bg={row.ACTIVO ? "success" : "secondary"}>
                              {row.ACTIVO ? "Sí" : "No"}
                            </Badge>
                          </td>
                          <td>
                            <Badge bg={isSynced(row) ? "primary" : "warning"}>
                              {isSynced(row) ? "Sí" : "No"}
                            </Badge>
                          </td>
                          <td>
                            <Button
                              size="sm"
                              variant="outline-primary"
                              disabled={reimporting || isOpenShift(row)}
                              onClick={() => handleReimportRow(row.ID)}
                              title={
                                isOpenShift(row)
                                  ? "Los turnos abiertos no se pueden sincronizar"
                                  : "Importar de Factorial"
                              }
                            >
                              <FaSyncAlt />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>

              <div className="d-md-none">
                {fichajes.length === 0 ? (
                  <p className="text-muted text-center">
                    No hay fichajes con estos filtros
                  </p>
                ) : (
                  fichajes.map((row) => (
                    <Card key={row.ID} className="mb-2">
                      <Card.Body>
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <Form.Check
                            type="checkbox"
                            checked={selectedIds.includes(row.ID)}
                            onChange={() => toggleSelect(row.ID)}
                            disabled={isOpenShift(row)}
                            label={row.EMPLEADO_NOMBRE || row.CREWMEMBER}
                            title={
                              isOpenShift(row)
                                ? "Los turnos abiertos no se pueden sincronizar"
                                : "Seleccionar fichaje"
                            }
                          />
                          <Button
                            size="sm"
                            variant="outline-primary"
                            disabled={reimporting || isOpenShift(row)}
                            onClick={() => handleReimportRow(row.ID)}
                            title={
                              isOpenShift(row)
                                ? "Los turnos abiertos no se pueden sincronizar"
                                : "Importar de Factorial"
                            }
                          >
                            <FaSyncAlt />
                          </Button>
                        </div>
                        <div className="small">
                          <div>Inicio: {formatDateTime(row.FECHA_INICIO)}</div>
                          <div>Fin: {formatDateTime(row.FECHA_FIN)}</div>
                          <div>Minutos: {row.MINUTOS ?? "-"}</div>
                          <div className="mt-1 d-flex gap-2">
                            <Badge bg={row.ACTIVO ? "success" : "secondary"}>
                              Activo: {row.ACTIVO ? "Sí" : "No"}
                            </Badge>
                            <Badge bg={isSynced(row) ? "primary" : "warning"}>
                              Sync: {isSynced(row) ? "Sí" : "No"}
                            </Badge>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  ))
                )}
              </div>

              {pagination.pages > 1 && (
                <Pagination className="justify-content-center mt-3">
                  <Pagination.Prev
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  />
                  {Array.from({ length: pagination.pages }, (_, i) => i + 1)
                    .filter(
                      (p) =>
                        p === 1 ||
                        p === pagination.pages ||
                        Math.abs(p - page) <= 1
                    )
                    .map((p, idx, arr) => {
                      const items = [];
                      if (idx > 0 && p - arr[idx - 1] > 1) {
                        items.push(
                          <Pagination.Ellipsis key={`e-${p}`} disabled />
                        );
                      }
                      items.push(
                        <Pagination.Item
                          key={p}
                          active={p === page}
                          onClick={() => setPage(p)}
                        >
                          {p}
                        </Pagination.Item>
                      );
                      return items;
                    })}
                  <Pagination.Next
                    disabled={page >= pagination.pages}
                    onClick={() =>
                      setPage((p) => Math.min(pagination.pages, p + 1))
                    }
                  />
                </Pagination>
              )}
              <div className="text-muted small text-center">
                {pagination.total} registro(s)
                {selectedIds.length > 0
                  ? ` · ${selectedIds.length} seleccionado(s)`
                  : ""}
              </div>
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default FichajesSyncList;
